/**
 * The scripted conversation the how-it-works stage plays on step 3.
 *
 * ## What it is arguing
 *
 * That the agent answers with INTERFACE, not prose — and that it refuses. Seven
 * exchanges, chosen so no two produce the same shape: an order placed with a
 * receipt, a bill explained with a chart, a batch of actions held for approval,
 * a REFUSAL, an export, an offboarding run, and a cited docs answer.
 *
 * The opening exchange is a PURCHASE rather than the refund it used to be, and
 * that is a positioning choice, not a copy edit. It is the first thing a
 * visitor watches the agent do, and a refund opened the demo on the customer
 * losing money and the operator cleaning up after a billing error — a support
 * queue. An order placed opens it on the thing the product is for: someone
 * asked, and something they wanted happened. The card shape is unchanged
 * (a headline figure over a three-row receipt), because the shape is what
 * makes this exchange different from the other six.
 *
 * The refusal (`workspace-delete`) is the load-bearing one and is deliberately
 * placed mid-script rather than last. A demo where the agent says yes seven
 * times is a demo of a chatbot; the one where it says "your role cannot do
 * this, and I never act above the person asking" is the product's actual
 * safety posture (`.claude/rules/security.md` — act-as-user, ASI03), and a
 * buyer's first question is always whether it can be told no.
 *
 * ## Shape
 *
 * Every string is a catalog KEY. Rows, bars and buttons are structural — the
 * numbers and labels they carry are the only literals here, because a currency
 * figure or a timestamp is content the same in every locale.
 */

import { BRAND } from '@/components/bedrock';

export type RowKind = 'ok' | 'next' | 'plus' | 'num' | 'who';

export interface CardRow {
  readonly kind: RowKind;
  /** Overrides the kind's default glyph (a number, or someone's initials). */
  readonly lead?: string;
  readonly labelKey: string;
  /**
   * A locale-NEUTRAL value: a clock time, a duration, a money figure. Rendered
   * verbatim, because there is nothing to translate in `12:04` or `$4,020`.
   */
  readonly value?: string;
  /**
   * A value that is a WORD, and therefore a translated one. "online" and "away"
   * are status words every locale has, and they sat as English literals beside
   * an already-localized `labelKey` — so a Hebrew visitor read a Hebrew label
   * against an English value in the same row.
   *
   * Deliberately a separate field rather than translating `value`: the ten
   * other values in the script are data, and running them through `t()` would
   * mean inventing ten catalog keys whose six translations are all identical.
   */
  readonly valueKey?: string;
}

export interface CardBar {
  readonly label: string;
  /** Width as a percentage of the track. */
  readonly width: string;
  readonly value: string;
  /** The highlighted bar — at most one per card. */
  readonly hot?: boolean;
}

export interface CardButton {
  readonly labelKey: string;
  readonly primary?: boolean;
}

/** How the exchange resolved. Drives the status chip's tone. */
export type Outcome = 'done' | 'waiting' | 'answered' | 'declined';

export interface Exchange {
  readonly id: string;
  readonly askKey: string;
  /** What the agent shows while it works ("checking billing"). */
  readonly workKey: string;
  readonly replyKey: string;
  readonly accent: string;
  readonly cardTitleKey: string;
  readonly cardMetaKey: string;
  /** A headline figure, when the answer has one. */
  readonly big?: string;
  readonly bigMetaKey?: string;
  readonly bars?: readonly CardBar[];
  readonly rows?: readonly CardRow[];
  readonly buttons?: readonly CardButton[];
  /** Citations — only ever on an answer drawn from the customer's docs. */
  readonly sources?: readonly string[];
  readonly chipKey: string;
  readonly outcome: Outcome;
}

const K = 'home.how.chat';

export const CHAT_SCRIPT: readonly Exchange[] = [
  {
    id: 'order',
    askKey: `${K}.order.ask`,
    workKey: `${K}.order.work`,
    replyKey: `${K}.order.reply`,
    accent: BRAND.blue,
    cardTitleKey: `${K}.order.card`,
    cardMetaKey: `${K}.order.meta`,
    big: '$2,400.00',
    bigMetaKey: `${K}.order.bigMeta`,
    rows: [
      { kind: 'ok', labelKey: `${K}.order.r1`, value: '12:04' },
      { kind: 'ok', labelKey: `${K}.order.r2`, value: '12:04' },
      { kind: 'next', labelKey: `${K}.order.r3`, value: '3–5d' },
    ],
    chipKey: `${K}.order.chip`,
    outcome: 'done',
  },
  {
    id: 'bill',
    askKey: `${K}.bill.ask`,
    workKey: `${K}.bill.work`,
    replyKey: `${K}.bill.reply`,
    accent: BRAND.cyan,
    cardTitleKey: `${K}.bill.card`,
    cardMetaKey: `${K}.bill.meta`,
    bars: [
      { label: 'Jan', width: '62%', value: '$4,020' },
      { label: 'Feb', width: '66%', value: '$4,180' },
      { label: 'Mar', width: '100%', value: '$5,940', hot: true },
    ],
    rows: [
      { kind: 'plus', lead: '+', labelKey: `${K}.bill.r1`, value: '+$1,320' },
      { kind: 'plus', lead: '+', labelKey: `${K}.bill.r2`, value: '+$440' },
    ],
    chipKey: `${K}.bill.chip`,
    outcome: 'answered',
  },
  {
    id: 'onboard',
    askKey: `${K}.onboard.ask`,
    workKey: `${K}.onboard.work`,
    replyKey: `${K}.onboard.reply`,
    accent: BRAND.violet,
    cardTitleKey: `${K}.onboard.card`,
    cardMetaKey: `${K}.onboard.meta`,
    rows: [
      { kind: 'num', lead: '01', labelKey: `${K}.onboard.r1`, value: '' },
      { kind: 'num', lead: '02', labelKey: `${K}.onboard.r2`, value: '' },
      { kind: 'num', lead: '03', labelKey: `${K}.onboard.r3`, value: '+$96/mo' },
      { kind: 'num', lead: '04', labelKey: `${K}.onboard.r4`, value: '' },
    ],
    buttons: [{ labelKey: `${K}.onboard.b1`, primary: true }, { labelKey: `${K}.onboard.b2` }],
    chipKey: `${K}.onboard.chip`,
    outcome: 'waiting',
  },
  {
    id: 'refuse',
    askKey: `${K}.refuse.ask`,
    workKey: `${K}.refuse.work`,
    replyKey: `${K}.refuse.reply`,
    accent: '#d97706',
    cardTitleKey: `${K}.refuse.card`,
    cardMetaKey: `${K}.refuse.meta`,
    rows: [
      { kind: 'who', lead: 'DW', labelKey: `${K}.refuse.r1`, valueKey: `${K}.status.online` },
      { kind: 'who', lead: 'PR', labelKey: `${K}.refuse.r2`, valueKey: `${K}.status.away` },
    ],
    buttons: [{ labelKey: `${K}.refuse.b1`, primary: true }],
    chipKey: `${K}.refuse.chip`,
    outcome: 'declined',
  },
  {
    id: 'export',
    askKey: `${K}.export.ask`,
    workKey: `${K}.export.work`,
    replyKey: `${K}.export.reply`,
    accent: '#34d399',
    cardTitleKey: `${K}.export.card`,
    cardMetaKey: `${K}.export.meta`,
    rows: [
      { kind: 'ok', labelKey: `${K}.export.r1`, value: '12:41' },
      { kind: 'ok', labelKey: `${K}.export.r2`, value: '12:41' },
    ],
    buttons: [{ labelKey: `${K}.export.b1` }],
    chipKey: `${K}.export.chip`,
    outcome: 'done',
  },
  {
    id: 'offboard',
    askKey: `${K}.offboard.ask`,
    workKey: `${K}.offboard.work`,
    replyKey: `${K}.offboard.reply`,
    accent: BRAND.blue,
    cardTitleKey: `${K}.offboard.card`,
    cardMetaKey: `${K}.offboard.meta`,
    rows: [
      { kind: 'ok', labelKey: `${K}.offboard.r1`, value: '' },
      { kind: 'ok', labelKey: `${K}.offboard.r2`, value: '' },
      { kind: 'ok', labelKey: `${K}.offboard.r3`, value: '' },
      { kind: 'ok', labelKey: `${K}.offboard.r4`, value: '' },
    ],
    chipKey: `${K}.offboard.chip`,
    outcome: 'done',
  },
  {
    id: 'sso',
    askKey: `${K}.sso.ask`,
    workKey: `${K}.sso.work`,
    replyKey: `${K}.sso.reply`,
    accent: '#8b93e0',
    cardTitleKey: `${K}.sso.card`,
    cardMetaKey: `${K}.sso.meta`,
    rows: [
      { kind: 'ok', labelKey: `${K}.sso.r1`, value: '' },
      { kind: 'ok', labelKey: `${K}.sso.r2`, value: '' },
    ],
    buttons: [{ labelKey: `${K}.sso.b1`, primary: true }, { labelKey: `${K}.sso.b2` }],
    sources: ['docs/sso-okta', 'security/scim'],
    chipKey: `${K}.sso.chip`,
    outcome: 'answered',
  },
];

/** Chip palette per outcome: `[background, ink]`, both AA on the card. */
export const OUTCOME_TONE: Record<Outcome, readonly [string, string]> = {
  done: ['rgba(52,211,153,.12)', '#0f766e'],
  waiting: ['rgba(245,158,11,.14)', '#8a5a06'],
  answered: ['#f3f3f6', '#56565e'],
  declined: ['rgba(12,13,16,.06)', '#3d3d47'],
};

/** Row-glyph palette per kind: `[background, ink, glyph]`. */
export const ROW_LEAD: Record<RowKind, readonly [string, string, string]> = {
  ok: ['rgba(52,211,153,.16)', '#0f766e', '✓'],
  next: ['rgba(43,98,255,.1)', '#2348db', '→'],
  plus: ['rgba(245,158,11,.16)', '#8a5a06', '+'],
  num: ['#f1f1f4', '#56565e', ''],
  who: ['#0c0d10', '#ffffff', ''],
};
